import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AUDIT_TEMPLATES } from "@/config/auditTemplates";
import { useResource } from "@/hooks/useResource";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, EmptyState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
  ClipboardCheck, ChevronDown, ChevronRight, Check, X, Minus,
  AlertTriangle, ArrowLeft, Loader2, Camera,
} from "lucide-react";

const uid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

// No object-storage endpoint exists on the shared backend, so evidence is stored
// inline as a compressed JPEG data-URL string in the checkpoint's `photos` array
// (the existing audit schema field). Compress to keep the document small.
function fileToDataUrl(file, max = 1024, quality = 0.7) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        c.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(c.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
const PRIORITY_STYLE = {
  critical: "bg-rose-100 text-rose-700",
  high: "bg-amber-100 text-amber-700",
  normal: "bg-stone-100 text-stone-600",
};

function buildBlankParameters(type) {
  return (AUDIT_TEMPLATES[type] || []).map((sec, i) => ({
    id: uid(), index: i + 1, name: sec.name, shortName: sec.shortName, key: sec.key,
    critical: !!sec.critical,
    checkpoints: sec.checkpoints.map((c) => ({
      id: uid(), name: c.name, priority: c.priority || "normal",
      status: null, notes: "", photos: [], correctiveAction: "", responsiblePerson: "", targetDate: "",
    })),
  }));
}

function computeScore(parameters) {
  let pass = 0, fail = 0, na = 0, critical = 0;
  parameters.forEach((s) => s.checkpoints.forEach((c) => {
    if (c.status === "pass") pass += 1;
    else if (c.status === "fail") { fail += 1; if (c.priority === "critical") critical += 1; }
    else if (c.status === "na") na += 1;
  }));
  const scored = pass + fail;
  const overallScore = scored ? Math.round((pass / scored) * 100) : 0;
  return { pass, fail, na, critical, overallScore, failedCount: fail, criticalCount: critical, scored };
}

function StatusButton({ active, tone, icon: Icon, label, onClick, testId }) {
  const tones = {
    pass: active ? "bg-emerald-600 text-white border-emerald-600" : "text-emerald-700 border-stone-200 hover:border-emerald-400",
    fail: active ? "bg-rose-600 text-white border-rose-600" : "text-rose-700 border-stone-200 hover:border-rose-400",
    na: active ? "bg-stone-500 text-white border-stone-500" : "text-stone-600 border-stone-200 hover:border-stone-400",
  };
  return (
    <button type="button" data-testid={testId} onClick={onClick}
      className={`flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors ${tones[tone]}`}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );
}

export default function AuditWorkflow({ auditType }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data, isLoading } = useResource("/audits");
  const audits = useMemo(() => {
    const arr = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
    return arr.filter((a) => String(a.auditType || "").toLowerCase() === auditType.toLowerCase())
      .sort((x, y) => new Date(y.dateTime || y.createdAt || 0) - new Date(x.dateTime || x.createdAt || 0));
  }, [data, auditType]);

  const [mode, setMode] = useState("list");
  const [auditId, setAuditId] = useState(null);
  const [bakeryName, setBakeryName] = useState("Freshezy Foodtech");
  const [location, setLocation] = useState("");
  const [auditorName, setAuditorName] = useState(user?.name || user?.email || "");
  const [parameters, setParameters] = useState([]);
  const [open, setOpen] = useState({});
  const [saving, setSaving] = useState(false);

  const score = useMemo(() => computeScore(parameters), [parameters]);
  const totalCheckpoints = parameters.reduce((s, p) => s + p.checkpoints.length, 0);
  const answered = score.pass + score.fail + score.na;

  const start = () => {
    const params = buildBlankParameters(auditType);
    setParameters(params);
    setAuditId(null);
    setBakeryName("Freshezy Foodtech");
    setLocation("");
    setAuditorName(user?.name || user?.email || "");
    setOpen(params.length ? { [params[0].id]: true } : {});
    setMode("run");
  };

  const setStatus = (si, ci, status) => {
    setParameters((prev) => prev.map((s, i) => i !== si ? s : {
      ...s, checkpoints: s.checkpoints.map((c, j) => j !== ci ? c : { ...c, status: c.status === status ? null : status }),
    }));
  };
  const setField = (si, ci, field, value) => {
    setParameters((prev) => prev.map((s, i) => i !== si ? s : {
      ...s, checkpoints: s.checkpoints.map((c, j) => j !== ci ? c : { ...c, [field]: value }),
    }));
  };
  const addPhotos = (si, ci) => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "image/*"; input.multiple = true;
    input.onchange = async (e) => {
      const files = Array.from(e.target.files || []);
      const urls = [];
      for (const f of files) { try { urls.push(await fileToDataUrl(f)); } catch (_) { /* skip */ } }
      if (urls.length) {
        setParameters((prev) => prev.map((s, i) => i !== si ? s : {
          ...s, checkpoints: s.checkpoints.map((c, j) => j !== ci ? c : { ...c, photos: [...(c.photos || []), ...urls] }),
        }));
        toast.success(`${urls.length} photo(s) attached`);
      }
    };
    input.click();
  };
  const removePhoto = (si, ci, idx) => {
    setParameters((prev) => prev.map((s, i) => i !== si ? s : {
      ...s, checkpoints: s.checkpoints.map((c, j) => j !== ci ? c : { ...c, photos: (c.photos || []).filter((_, k) => k !== idx) }),
    }));
  };
  const viewPhoto = (dataUrl) => {
    const w = window.open();
    if (w) w.document.write(`<img src="${dataUrl}" style="max-width:100%"/>`);
  };

  const autoCreateCapa = async (aid) => {
    const fails = [];
    parameters.forEach((sec) => sec.checkpoints.forEach((c) => {
      if (c.priority === "critical" && c.status === "fail") fails.push({ sec, c });
    }));
    if (!fails.length) return 0;
    let existing = [];
    try {
      const res = await api.get("/capa");
      existing = Array.isArray(res.data) ? res.data : res.data?.items || [];
    } catch (_) { /* ignore */ }
    const have = new Set(existing.filter((x) => x.linkedAuditId === aid).map((x) => x.findingRef));
    let created = 0;
    for (const { sec, c } of fails) {
      const ref = `${sec.key}:${c.id}`;
      if (have.has(ref)) continue;
      try {
        await api.post("/capa", {
          type: "CAPA",
          title: `[${auditType} Audit] ${c.name}`,
          description: `Critical checkpoint FAILED during ${auditType} audit at ${bakeryName}.${c.notes ? " " + c.notes : ""}`,
          source: "AUTO-CREATED FROM AUDIT",
          linkedAuditId: aid, linkedAuditType: auditType, findingRef: ref,
          severity: "Critical", category: sec.name,
          correctiveAction: c.correctiveAction || "", owner: c.responsiblePerson || "",
          dueDate: c.targetDate || "", photos: c.photos || [],
          remarks: `Auditor: ${auditorName} · ${new Date().toLocaleString()}`,
        });
        created += 1;
      } catch (_) { /* continue */ }
    }
    return created;
  };

  const save = async (status) => {
    if (!auditorName.trim()) { toast.error("Auditor name is required"); return; }
    const sc = computeScore(parameters);
    const payload = {
      bakeryName, location, auditorName, auditType, status,
      dateTime: new Date().toISOString(),
      overallScore: sc.overallScore, criticalCount: sc.criticalCount, failedCount: sc.failedCount,
      parameters,
    };
    setSaving(true);
    try {
      let aid = auditId;
      if (aid) {
        await api.put(`/audits/${aid}`, payload);
      } else {
        const res = await api.post("/audits", payload);
        aid = res.data?.id || res.data?._id || null;
        setAuditId(aid);
      }
      if (status === "Completed" && aid) {
        const n = await autoCreateCapa(aid);
        toast.success(`${auditType} audit completed (${sc.overallScore}%)${n ? ` · ${n} CAPA auto-created` : ""}`);
      } else {
        toast.success(status === "Completed" ? `${auditType} audit completed (${sc.overallScore}%)` : "Draft saved");
      }
      qc.invalidateQueries({ queryKey: ["resource", "/audits"] });
      if (status === "Completed") setMode("list");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  if (mode === "run") {
    return (
      <div className="space-y-4">
        <PageHeader title={`${auditType} Audit`} subtitle="QA / FSSAI checklist · shared ERP">
          <Button variant="outline" className="gap-2" data-testid="audit-back" onClick={() => setMode("list")}>
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
        </PageHeader>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
          {/* Checklist */}
          <div className="space-y-4 order-2 lg:order-1">
            {/* header fields */}
            <div className="grid grid-cols-1 gap-3 rounded-xl border border-stone-200 bg-white p-4 shadow-sm sm:grid-cols-3">
              <div><Label className="text-xs">Unit / Bakery</Label>
                <Input data-testid="audit-bakery" value={bakeryName} onChange={(e) => setBakeryName(e.target.value)} /></div>
              <div><Label className="text-xs">Location</Label>
                <Input data-testid="audit-location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Optional" /></div>
              <div><Label className="text-xs">Auditor</Label>
                <Input data-testid="audit-auditor" value={auditorName} onChange={(e) => setAuditorName(e.target.value)} /></div>
            </div>

            {parameters.map((sec, si) => {
              const secScore = sec.checkpoints.filter((c) => c.status).length;
              const isOpen = !!open[sec.id];
              return (
                <div key={sec.id} className="rounded-xl border border-stone-200 bg-white shadow-sm">
                  <button type="button" data-testid={`audit-section-${sec.key}`}
                    onClick={() => setOpen((o) => ({ ...o, [sec.id]: !o[sec.id] }))}
                    className="flex w-full items-center justify-between px-4 py-3 text-left">
                    <div className="flex items-center gap-2">
                      {isOpen ? <ChevronDown className="h-4 w-4 text-stone-400" /> : <ChevronRight className="h-4 w-4 text-stone-400" />}
                      <span className="font-display font-semibold text-stone-900">{sec.name}</span>
                      {sec.critical && <Badge className="bg-rose-100 text-rose-700">Critical</Badge>}
                    </div>
                    <span className="text-xs text-stone-500">{secScore}/{sec.checkpoints.length}</span>
                  </button>
                  {isOpen && (
                    <div className="divide-y divide-stone-100 border-t border-stone-100">
                      {sec.checkpoints.map((c, ci) => (
                        <div key={c.id} className="px-4 py-3">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex min-w-0 flex-1 items-center gap-2">
                              <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${PRIORITY_STYLE[c.priority] || PRIORITY_STYLE.normal}`}>{c.priority}</span>
                              <span className="text-sm text-stone-800">{c.name}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <StatusButton testId={`audit-cp-${sec.key}-${ci}-pass`} active={c.status === "pass"} tone="pass" icon={Check} label="Pass" onClick={() => setStatus(si, ci, "pass")} />
                              <StatusButton testId={`audit-cp-${sec.key}-${ci}-fail`} active={c.status === "fail"} tone="fail" icon={X} label="Fail" onClick={() => setStatus(si, ci, "fail")} />
                              <StatusButton testId={`audit-cp-${sec.key}-${ci}-na`} active={c.status === "na"} tone="na" icon={Minus} label="N/A" onClick={() => setStatus(si, ci, "na")} />
                            </div>
                          </div>
                          {c.status === "fail" && (
                            <div className="mt-2 grid grid-cols-1 gap-2 rounded-lg bg-rose-50/60 p-2 sm:grid-cols-2">
                              <Textarea data-testid={`audit-cp-${sec.key}-${ci}-ca`} value={c.correctiveAction} onChange={(e) => setField(si, ci, "correctiveAction", e.target.value)} placeholder="Corrective action…" className="min-h-[38px] bg-white text-sm" />
                              <div className="grid grid-cols-2 gap-2">
                                <Input value={c.responsiblePerson} onChange={(e) => setField(si, ci, "responsiblePerson", e.target.value)} placeholder="Responsible" className="bg-white text-sm" />
                                <Input type="date" value={c.targetDate} onChange={(e) => setField(si, ci, "targetDate", e.target.value)} className="bg-white text-sm" />
                              </div>
                            </div>
                          )}
                          {(c.status === "fail" || c.status === "na") && (
                            <Input value={c.notes} onChange={(e) => setField(si, ci, "notes", e.target.value)} placeholder="Notes / observation" className="mt-2 text-sm" />
                          )}
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <button type="button" data-testid={`audit-cp-${sec.key}-${ci}-addphoto`}
                              onClick={() => addPhotos(si, ci)}
                              className="inline-flex items-center gap-1 rounded-md border border-dashed border-stone-300 px-2 py-1 text-xs text-stone-600 hover:border-amber-400 hover:text-amber-700">
                              <Camera className="h-3.5 w-3.5" /> Add Photo
                            </button>
                            {(c.photos || []).map((ph, pi) => (
                              <div key={pi} className="relative">
                                <img src={ph} alt="evidence" data-testid={`audit-cp-${sec.key}-${ci}-photo-${pi}`}
                                  onClick={() => viewPhoto(ph)}
                                  className="h-11 w-11 cursor-pointer rounded-md border border-stone-200 object-cover" />
                                <button type="button" onClick={() => removePhoto(si, ci, pi)}
                                  className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-rose-600 text-white">
                                  <X className="h-2.5 w-2.5" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Sticky summary */}
          <div className="order-1 lg:order-2">
            <div className="sticky top-4 space-y-3 rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
              <div className="text-[11px] font-bold uppercase tracking-wider text-stone-400">Live Score</div>
              <div className="font-display text-4xl font-bold text-stone-900">{score.overallScore}%</div>
              <div className="text-xs text-stone-500">{answered}/{totalCheckpoints} checkpoints answered</div>
              <div className="grid grid-cols-2 gap-2 pt-2 text-sm">
                <div className="rounded bg-emerald-50 px-2 py-1 text-emerald-700">Pass: <strong>{score.pass}</strong></div>
                <div className="rounded bg-rose-50 px-2 py-1 text-rose-700">Fail: <strong>{score.fail}</strong></div>
                <div className="rounded bg-stone-50 px-2 py-1 text-stone-600">N/A: <strong>{score.na}</strong></div>
                <div className="rounded bg-rose-100 px-2 py-1 text-rose-800">Critical: <strong>{score.critical}</strong></div>
              </div>
              {score.critical > 0 && (
                <div className="flex items-start gap-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {score.critical} critical checkpoint(s) failed. Record corrective actions.
                </div>
              )}
              <div className="space-y-2 pt-2">
                <Button data-testid="audit-save-draft" variant="outline" className="w-full" disabled={saving} onClick={() => save("In Progress")}>
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save Draft
                </Button>
                <Button data-testid="audit-complete" className="w-full bg-emerald-700 hover:bg-emerald-800" disabled={saving || answered === 0} onClick={() => save("Completed")}>
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Complete Audit
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title={`${auditType} Audit`} subtitle="QA / FSSAI operational checklist · shared ERP">
        <Button data-testid={`start-${auditType.toLowerCase()}-audit-btn`} className="gap-2 bg-emerald-700 hover:bg-emerald-800" onClick={start}>
          <ClipboardCheck className="h-4 w-4" /> Start {auditType} Audit
        </Button>
      </PageHeader>

      <div>
        <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-stone-400">Audit History</div>
        {isLoading ? (
          <LoadingState label="Loading audits…" />
        ) : audits.length === 0 ? (
          <EmptyState title={`No ${auditType.toLowerCase()} audits yet`} message={`Tap "Start ${auditType} Audit" to run your first checklist.`} />
        ) : (
          <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-left text-xs uppercase tracking-wide text-stone-500">
                <tr>
                  <th className="px-4 py-2.5">Unit</th><th className="px-4 py-2.5">Auditor</th>
                  <th className="px-4 py-2.5">Date</th><th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5 text-right">Score</th><th className="px-4 py-2.5 text-right">Critical</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {audits.map((a) => (
                  <tr key={a.id} data-testid={`audit-row-${a.id}`} className="hover:bg-stone-50/60">
                    <td className="px-4 py-2.5 font-medium text-stone-800">{a.bakeryName || "—"}</td>
                    <td className="px-4 py-2.5 text-stone-600">{a.auditorName || "—"}</td>
                    <td className="px-4 py-2.5 text-stone-600">{a.dateTime ? new Date(a.dateTime).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-2.5"><Badge className={a.status === "Completed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>{a.status}</Badge></td>
                    <td className="px-4 py-2.5 text-right font-semibold">{typeof a.overallScore === "number" ? `${Math.round(a.overallScore)}%` : "—"}</td>
                    <td className="px-4 py-2.5 text-right">{a.criticalCount || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
